/**
 * Encore Foundation - 3.8.0
 *
 * The contents of this file is owned and generated by the Global Design Systems team.
 * If you require making changes to the values stored here please reach out
 * to #encore on Slack.
 *
 * Links:
 *
 * - Source & Documentation: https://ghe.spotify.net/encore/foundation
 *
 * Tack!
 *
 * Global Design Systems
 * encore.spotify.net
 */

/**
 * ![Status: production](https://img.shields.io/badge/PRODUCTION-green|height=14)
 * The Dark theme is used for our consumer products, like the Desktop client, Web Player, and the consumer mobile clients.
 */
export const darkTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#242424',
        highlight: '#2a2a2a',
        press: '#000000',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#f15e6c',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#ffa42b',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#3d91f4',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#ffa42b',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#12833a',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#17a449',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.1)',
        highlight: 'rgba(255, 255, 255, 0.13)',
        press: 'rgba(255, 255, 255, 0.06)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#9e9e9e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: production](https://img.shields.io/badge/PRODUCTION-green|height=14)
 * The Light theme is used for marketing contexts, like spotify.com, B2B products like Ad Studio, internal tools and generally when data density is high and text legibility is important.
 */
export const lightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#ffffff',
        highlight: '#f6f6f6',
        press: '#b7b7b7',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#117a37',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#d31225',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9d5a00',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117a37',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#0c67d3',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#878787',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#169b45',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c77200',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#169b45',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#12833a',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#17a449',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#1ed760',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: production](https://img.shields.io/badge/PRODUCTION-green|height=14)
 * The Creator Dark theme is used for creator products, when building flows or illustrating views from the consumer app, like profile editing or canvas submission in S4A.
 */
export const creatorDarkTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#242424',
        highlight: '#2a2a2a',
        press: '#000000',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9979ff',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#f15e6c',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#ffa42b',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#3d91f4',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#794fff',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#ffa42b',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#5925ff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#4e17ff',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#25009d',
      elevated: {
        /**
         * Fallback color
         */
        base: '#5925ff',

        /**
         * Fallback color
         */
        highlight: '#5925ff',

        /**
         * Fallback color
         */
        press: '#5925ff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#5925ff',

        /**
         * Fallback color
         */
        highlight: '#5925ff',

        /**
         * Fallback color
         */
        press: '#5925ff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#5925ff',

        /**
         * Fallback color
         */
        highlight: '#5925ff',

        /**
         * Fallback color
         */
        press: '#5925ff',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#7347ff',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#825aff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5d2aff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5925ff',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5925ff',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.1)',
        highlight: 'rgba(255, 255, 255, 0.13)',
        press: 'rgba(255, 255, 255, 0.06)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#9e9e9e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#906eff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#6f42ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: production](https://img.shields.io/badge/PRODUCTION-green|height=14)
 * The Creator Light theme is used for creator products, like Spotify for Artists, and Spotify for Publishers.
 */
export const creatorLightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#ffffff',
        highlight: '#f6f6f6',
        press: '#b7b7b7',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#4100f4',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#d31225',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9d5a00',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117a37',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#0c67d3',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#878787',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#4100f4',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c77200',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#169b45',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#4100f4',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#3d00e6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#1d006c',
      elevated: {
        /**
         * Fallback color
         */
        base: '#4100f4',

        /**
         * Fallback color
         */
        highlight: '#4100f4',

        /**
         * Fallback color
         */
        press: '#4100f4',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#4100f4',

        /**
         * Fallback color
         */
        highlight: '#4100f4',

        /**
         * Fallback color
         */
        press: '#4100f4',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#4100f4',

        /**
         * Fallback color
         */
        highlight: '#4100f4',

        /**
         * Fallback color
         */
        press: '#4100f4',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#662fff',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#8559ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#6127ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#4100f4',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#4100f4',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9c78ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7d4dff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: experimental](https://img.shields.io/badge/EXPERIMENTAL-yellow|height=14)
 * The Overtime Dark theme is used for the Overtime product.
 */
export const overtimeDarkTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#242424',
        highlight: '#2a2a2a',
        press: '#000000',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#f15e6c',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#ffa42b',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#3d91f4',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#ffa42b',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#19e68c',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#4beba5',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#12a967',
      elevated: {
        /**
         * Fallback color
         */
        base: '#19e68c',

        /**
         * Fallback color
         */
        highlight: '#19e68c',

        /**
         * Fallback color
         */
        press: '#19e68c',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#19e68c',

        /**
         * Fallback color
         */
        highlight: '#19e68c',

        /**
         * Fallback color
         */
        press: '#19e68c',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#19e68c',

        /**
         * Fallback color
         */
        highlight: '#19e68c',

        /**
         * Fallback color
         */
        press: '#19e68c',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#16c97a',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#0e824f',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#12a363',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.1)',
        highlight: 'rgba(255, 255, 255, 0.13)',
        press: 'rgba(255, 255, 255, 0.06)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#9e9e9e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#19e68c',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: experimental](https://img.shields.io/badge/EXPERIMENTAL-yellow|height=14)
 * The Advertising Light theme is used for advertising products, like Ad Studio, Ad Studio Admin, and the ads.spotify.com marketing page.
 */
export const advertisingLightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#ffffff',
        highlight: '#f6f6f6',
        press: '#b7b7b7',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#d31225',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9d5a00',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117a37',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#0c67d3',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#878787',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c77200',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#169b45',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#734d85',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#6c497d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3b2744',
      elevated: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#895c9f',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9469a8',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#764f88',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#a683b7',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#8e61a3',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: experimental](https://img.shields.io/badge/EXPERIMENTAL-yellow|height=14)
 * The Advertising Dark theme is used for advertising products, like Ad Studio, Ad Studio Admin, and the ads.spotify.com marketing page.
 */
export const advertisingDarkTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#242424',
        highlight: '#2a2a2a',
        press: '#000000',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#a683b7',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#f15e6c',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#ffa42b',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#3d91f4',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#8e61a3',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#ffa42b',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#1ed760',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#734d85',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#6c497d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3b2744',
      elevated: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#734d85',

        /**
         * Fallback color
         */
        highlight: '#734d85',

        /**
         * Fallback color
         */
        press: '#734d85',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#895c9f',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9469a8',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#764f88',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#734d85',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.1)',
        highlight: 'rgba(255, 255, 255, 0.13)',
        press: 'rgba(255, 255, 255, 0.06)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#9e9e9e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9f79b2',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#85599a',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: production](https://img.shields.io/badge/PRODUCTION-green|height=14)
 * The Quicksilver Light theme is used for Quicksilver, an internal Spotify product.
 */
export const quicksilverLightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#ffffff',
        highlight: '#f6f6f6',
        press: '#b7b7b7',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#543edd',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#d31225',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9d5a00',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117a37',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#0c67d3',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#878787',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#543edd',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c77200',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#169b45',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#0d72ea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#543edd',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#4d36dc',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#271885',
      elevated: {
        /**
         * Fallback color
         */
        base: '#543edd',

        /**
         * Fallback color
         */
        highlight: '#543edd',

        /**
         * Fallback color
         */
        press: '#543edd',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#543edd',

        /**
         * Fallback color
         */
        highlight: '#543edd',

        /**
         * Fallback color
         */
        press: '#543edd',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#543edd',

        /**
         * Fallback color
         */
        highlight: '#543edd',

        /**
         * Fallback color
         */
        press: '#543edd',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#6a57e1',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#0d72ea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0c6ddf',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#084791',
      elevated: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#0d72ea',

        /**
         * Fallback color
         */
        highlight: '#0d72ea',

        /**
         * Fallback color
         */
        press: '#0d72ea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#2a86f3',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7867e4',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5842de',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#543edd',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#543edd',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9082e9',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#705de3',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: experimental](https://img.shields.io/badge/EXPERIMENTAL-yellow|height=14)
 * The Anchor Light theme is used for Anchor.fm, a product used to create, distribute, and monetize podcasts.
 */
export const anchorLightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#fbfbfb',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f2f2f2',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b4b4b4',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#fbfbfb',
        highlight: '#f2f2f2',
        press: '#b4b4b4',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#fbfbfb',

        /**
         * Fallback color
         */
        highlight: '#fbfbfb',

        /**
         * Fallback color
         */
        press: '#fbfbfb',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#2a2837',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#686389',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5000b9',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#cf1224',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9a5800',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117835',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#7d3fe8',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#2a2837',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8580a3',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5000b9',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c36f00',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#159844',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#874eea',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#2a2837',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dadada',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#5000b9',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#4a00ac',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#080014',
      elevated: {
        /**
         * Fallback color
         */
        base: '#5000b9',

        /**
         * Fallback color
         */
        highlight: '#5000b9',

        /**
         * Fallback color
         */
        press: '#5000b9',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#5000b9',

        /**
         * Fallback color
         */
        highlight: '#5000b9',

        /**
         * Fallback color
         */
        press: '#5000b9',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#5000b9',

        /**
         * Fallback color
         */
        highlight: '#5000b9',

        /**
         * Fallback color
         */
        press: '#5000b9',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#6500e9',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#874eea',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#8146e9',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#5116b8',
      elevated: {
        /**
         * Fallback color
         */
        base: '#874eea',

        /**
         * Fallback color
         */
        highlight: '#874eea',

        /**
         * Fallback color
         */
        press: '#874eea',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#874eea',

        /**
         * Fallback color
         */
        highlight: '#874eea',

        /**
         * Fallback color
         */
        press: '#874eea',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#874eea',

        /**
         * Fallback color
         */
        highlight: '#874eea',

        /**
         * Fallback color
         */
        press: '#874eea',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#9766ed',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#9a4dff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#750bff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5000b9',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#5000b9',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#24203f',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#2a254a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      elevated: {
        /**
         * The default background color for elevated elements in the mutedAccent set.
         */
        base: '#312c56',
        highlight: '#373160',
        press: '#000000',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.08)',
        highlight: 'rgba(255, 255, 255, 0.11)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#24203f',

        /**
         * Fallback color
         */
        highlight: '#24203f',

        /**
         * Fallback color
         */
        press: '#24203f',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#b9b4d9',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#e8f524',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8077ba',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#e8f524',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#36305e',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};

/**
 * ![Status: experimental](https://img.shields.io/badge/EXPERIMENTAL-yellow|height=14)
 * The Megaphone Light theme is used for the Megaphone CMS, a product to publish, monetize, and measure podcasts.
 */
export const megaphoneLightTheme = {
  base: {
    background: {
      /**
       * The default background color of the base set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * The default background color for elevated elements in the base set.
         */
        base: '#ffffff',
        highlight: '#f6f6f6',
        press: '#b7b7b7',
      },
      tinted: {
        /**
         * The default background color for tinted elements in the base set.
         */
        base: 'rgba(0, 0, 0, 0.08)',
        highlight: 'rgba(0, 0, 0, 0.05)',
        press: 'rgba(0, 0, 0, 0.12)',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#6a6a6a',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#6447ff',

      /**
       * A text color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#d31225',

      /**
       * A text color associated with caution. Often used to highlight potential problems or slightly negative metrics or data.
       */
      warning: '#9d5a00',

      /**
       * A text color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#117a37',

      /**
       * A text color associated with system announcements and notifications.
       */
      announcement: '#496d91',
    },
    essential: {
      /**
       * The default essential color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#878787',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7358ff',

      /**
       * An essential color associated with negativity. Often used for errors, destructive actions, or to highlight negative metrics or data.
       */
      negative: '#e91429',

      /**
       * An essential color associated with caution. Often used to highlight potential problems or slightly negative metrics or data
       */
      warning: '#c77200',

      /**
       * An essential color associated with positivity. Often used for success states, or to highlight positive metrics or data.
       */
      positive: '#169b45',

      /**
       * An essential color associated with system announcements and notifications.
       */
      announcement: '#5179a1',
    },
    decorative: {
      /**
       * The default decorative color of the base set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  brightAccent: {
    background: {
      /**
       * The default background color of the brightAccent set.
       */
      base: '#7358ff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#6c50ff',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#2600eb',
      elevated: {
        /**
         * Fallback color
         */
        base: '#7358ff',

        /**
         * Fallback color
         */
        highlight: '#7358ff',

        /**
         * Fallback color
         */
        press: '#7358ff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#7358ff',

        /**
         * Fallback color
         */
        highlight: '#7358ff',

        /**
         * Fallback color
         */
        press: '#7358ff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#7358ff',

        /**
         * Fallback color
         */
        highlight: '#7358ff',

        /**
         * Fallback color
         */
        press: '#7358ff',
      },
    },
    text: {
      /**
       * The default text color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the brightAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#876fff',
    },
  },
  negative: {
    background: {
      /**
       * The default background color of the negative set.
       */
      base: '#e91429',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#de1327',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#920d1a',
      elevated: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#e91429',

        /**
         * Fallback color
         */
        highlight: '#e91429',

        /**
         * Fallback color
         */
        press: '#e91429',
      },
    },
    text: {
      /**
       * The default text color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the negative set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the negative set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#ee3a4c',
    },
  },
  warning: {
    background: {
      /**
       * The default background color of the warning set.
       */
      base: '#ffa42b',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#ffaf45',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#c57100',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffa42b',

        /**
         * Fallback color
         */
        highlight: '#ffa42b',

        /**
         * Fallback color
         */
        press: '#ffa42b',
      },
    },
    text: {
      /**
       * The default text color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the warning set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the warning set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#f18900',
    },
  },
  positive: {
    background: {
      /**
       * The default background color of the positive set.
       */
      base: '#1ed760',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1fdf64',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#169c46',
      elevated: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#1ed760',

        /**
         * Fallback color
         */
        highlight: '#1ed760',

        /**
         * Fallback color
         */
        press: '#1ed760',
      },
    },
    text: {
      /**
       * The default text color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the positive set.
       */
      base: '#000000',

      /**
       * Fallback color
       */
      subdued: '#000000',

      /**
       * Fallback color
       */
      brightAccent: '#000000',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the positive set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1abc54',
    },
  },
  announcement: {
    background: {
      /**
       * The default background color of the announcement set.
       */
      base: '#5179a1',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#4d7399',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#324a63',
      elevated: {
        /**
         * Fallback color
         */
        base: '#5179a1',

        /**
         * Fallback color
         */
        highlight: '#5179a1',

        /**
         * Fallback color
         */
        press: '#5179a1',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#5179a1',

        /**
         * Fallback color
         */
        highlight: '#5179a1',

        /**
         * Fallback color
         */
        press: '#5179a1',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#5179a1',

        /**
         * Fallback color
         */
        highlight: '#5179a1',

        /**
         * Fallback color
         */
        press: '#5179a1',
      },
    },
    text: {
      /**
       * The default text color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the announcement set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the announcement set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#658bb2',
    },
  },
  invertedDark: {
    background: {
      /**
       * The default background color of the invertedDark set.
       */
      base: '#000000',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#0d0d0d',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#3f3f3f',
      elevated: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#000000',

        /**
         * Fallback color
         */
        highlight: '#000000',

        /**
         * Fallback color
         */
        press: '#000000',
      },
    },
    text: {
      /**
       * The default text color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#785eff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7358ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the invertedDark set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#1f1f1f',
    },
  },
  invertedLight: {
    background: {
      /**
       * The default background color of the invertedLight set.
       */
      base: '#ffffff',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#f6f6f6',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#b7b7b7',
      elevated: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#ffffff',

        /**
         * Fallback color
         */
        highlight: '#ffffff',

        /**
         * Fallback color
         */
        press: '#ffffff',
      },
    },
    text: {
      /**
       * The default text color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#5e5e5e',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#6e52ff',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    essential: {
      /**
       * The default essential color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#8f8f8f',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7358ff',

      /**
       * Fallback color
       */
      negative: '#000000',

      /**
       * Fallback color
       */
      warning: '#000000',

      /**
       * Fallback color
       */
      positive: '#000000',

      /**
       * Fallback color
       */
      announcement: '#000000',
    },
    decorative: {
      /**
       * The default decorative color of the invertedLight set.
       */
      base: '#000000',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#dedede',
    },
  },
  mutedAccent: {
    background: {
      /**
       * The default background color of the mutedAccent set.
       */
      base: '#121212',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: '#1a1a1a',

      /**
       * A background color that indicates the press state of a component.
       */
      press: '#000000',
      tinted: {
        /**
         * The default background color for tinted elements in the mutedAccent set.
         */
        base: 'rgba(255, 255, 255, 0.07)',
        highlight: 'rgba(255, 255, 255, 0.1)',
        press: 'rgba(255, 255, 255, 0.04)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
      unsafeForSmallText: {
        /**
         * Fallback color
         */
        base: '#121212',

        /**
         * Fallback color
         */
        highlight: '#121212',

        /**
         * Fallback color
         */
        press: '#121212',
      },
    },
    text: {
      /**
       * The default text color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base text color.
       */
      subdued: '#a7a7a7',

      /**
       * A bright text color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#917cff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base essential color.
       */
      subdued: '#727272',

      /**
       * A bright essential color that contrasts with the set’s background colors. Often used to give key interactive elements more prominence.
       */
      brightAccent: '#7358ff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the mutedAccent set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: '#292929',
    },
  },
  overMedia: {
    background: {
      /**
       * The default background color of the overMedia set.
       */
      base: 'rgba(0, 0, 0, 0.53)',

      /**
       * A background color that indicates the hover state of a component, or adds some oomph to a focus state.
       */
      highlight: 'rgba(0, 0, 0, 0.56)',

      /**
       * A background color that indicates the press state of a component.
       */
      press: 'rgba(0, 0, 0, 0.71)',
      unsafeForSmallText: {
        /**
         * The default background color for Unsafe for Small Text elements in the Over Media set.
         */
        base: 'rgba(0, 0, 0, 0.42)',
        highlight: 'rgba(0, 0, 0, 0.44)',
        press: 'rgba(0, 0, 0, 0.61)',
      },
      elevated: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
      tinted: {
        /**
         * Fallback color
         */
        base: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        highlight: 'rgba(0, 0, 0, 0.53)',

        /**
         * Fallback color
         */
        press: 'rgba(0, 0, 0, 0.53)',
      },
    },
    text: {
      /**
       * The default text color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    essential: {
      /**
       * The default essential color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * Fallback color
       */
      subdued: '#ffffff',

      /**
       * Fallback color
       */
      brightAccent: '#ffffff',

      /**
       * Fallback color
       */
      negative: '#ffffff',

      /**
       * Fallback color
       */
      warning: '#ffffff',

      /**
       * Fallback color
       */
      positive: '#ffffff',

      /**
       * Fallback color
       */
      announcement: '#ffffff',
    },
    decorative: {
      /**
       * The default decorative color of the overMedia set.
       */
      base: '#ffffff',

      /**
       * A subdued variant of the base decorative color.
       */
      subdued: 'rgba(255, 255, 255, 0.13)',
    },
  },
};
export const theme = {
  darkTheme,
  lightTheme,
  creatorDarkTheme,
  creatorLightTheme,
  overtimeDarkTheme,
  advertisingLightTheme,
  advertisingDarkTheme,
  quicksilverLightTheme,
  anchorLightTheme,
  megaphoneLightTheme,
};
